import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import topIcon from "@/assets/images/top.svg";
import Disclaimer from "@/components/common/Disclaimer";
import BrandNotFoundModal from "@/components/modals/BrandNotFoundModal";
import BaseAnalysisSection from "@/components/SearchDetail/BaseAnalysisSection";
import CitationAnalysisSection from "@/components/SearchDetail/CitationAnalysisSection";
import CompetitorOverviewSection from "@/components/SearchDetail/CompetitorOverviewSection";
import InsightAnalysisSection from "@/components/SearchDetail/InsightAnalysisSection";
import SentimentAnalysisSection from "@/components/SearchDetail/SentimentAnalysisSection";
import { SearchPanelCard } from "@/components/SearchPanel/SearchPanelCard";
import { useScrollLock } from "@/hooks/useScrollLock";
import useSearchDetailFlow from "@/hooks/useSearchDetailFlow";
import { useSearchPanel } from "@/hooks/useSearchPanel";
import type { SearchRow } from "@/services/api/search";
import { App as AntApp, Drawer, Grid } from "antd";

import { FilterOutlined, LeftOutlined } from "@ant-design/icons";
import "./index.less";

export const SearchDetail: React.FC = () => {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const { search: locationSearch } = useLocation();
  const params = new URLSearchParams(locationSearch);
  const brand = params.get("brand") || "";
  const industry = params.get("industry") || "";
  const brandId = params.get("brandId") || "";
  const industryId = params.get("industryId") || "";
  // const modelName = params.get('modelName') || ''
  const searchQuery = params.get("q") || "";
  const startDate = params.get("startDate") || "";
  const endDate = params.get("endDate") || "";
  const qId = params.get("qId") || "";
  // 滚动监听状态
  const [hasTriggeredSecondPage, setHasTriggeredSecondPage] = useState(false);
  const [hasTriggeredToday, setHasTriggeredToday] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);
  // 添加弹窗状态
  const [showBrandNotFoundModal, setShowBrandNotFoundModal] = useState(false);
  // 添加搜索品牌名称状态
  const [searchedBrandName, setSearchedBrandName] = useState("");
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [isSticky, setIsSticky] = useState(false);
  // 用于跟踪吸顶状态，避免滚动时频繁触发渲染
  const lastIsStickyRef = React.useRef(false);
  // 筛选器收起状态
  const [isFilterCollapsed, setIsFilterCollapsed] = useState(false);

  // 获取所有行业数据
  const industriesParam = params.get("industries");
  const industryIdsParam = params.get("industryIds");
  const industries = industriesParam ? JSON.parse(industriesParam) : [industry];
  const industryIds = industryIdsParam
    ? JSON.parse(industryIdsParam)
    : [industryId];

  // 构建specificItem（如果有brandId和industryId以及对应的名称）
  const specificItem =
    brandId && industryId
      ? {
          brandId,
          industryId,
          brandName: brand || undefined,
          industryName: industry || undefined,
        }
      : undefined;

  // 状态定义
  const [brandName] = React.useState(brand || "");
  const [industrySel, setIndustrySel] = React.useState(industry || "");
  const [selectedCompetitors, setSelectedCompetitors] = React.useState<
    string[]
  >([]);
  const [selectedPlatforms, setSelectedPlatforms] = React.useState<string[]>(
    []
  );
  // 初始化时间范围：优先从 URL 获取 startDate 和 endDate
  const [currentTimeRange, setCurrentTimeRange] = React.useState<
    | "today"
    | "last7d"
    | "last30d"
    | number
    | { startDate: string; endDate: string }
  >(() => {
    if (startDate && endDate) {
      return { startDate, endDate };
    }
    return "last7d";
  });

  // 计算当前的天数和modelId
  const currentDays =
    typeof currentTimeRange === "object"
      ? 0 // 自定义时间范围时，days 不重要，hook 会优先使用 startDate/endDate
      : typeof currentTimeRange === "number"
        ? currentTimeRange
        : currentTimeRange === "today"
          ? 1
          : currentTimeRange === "last7d"
            ? 7
            : 30;
  const currentModelId =
    selectedPlatforms.length === 0 ? "all" : selectedPlatforms[0];

  const {
    models,
    brandMetrics,
    brandMetricsTrend,
    brandAiMetricsTrend,
    brandComparison, // 新增：品牌对比数据
    brandRatesList,
    reputationKeywords, // 新增：品牌声誉关键词数据
    mediaCite,
    contentCite,
    // searchResults,
    isLoading: flowLoading,
    executeSearchFlow: triggerSearchFlow,
  } = useSearchDetailFlow({
    brandId,
    categoryId: industryId,
    keyword: searchQuery,
    specificItem, // 传递完整的品牌信息
    autoExecute: true,
    currentDays, // 传递当前选择的时间范围
    currentModelId, // 传递当前选择的AI平台
    startDate:
      typeof currentTimeRange === "object"
        ? currentTimeRange.startDate
        : undefined,
    endDate:
      typeof currentTimeRange === "object"
        ? currentTimeRange.endDate
        : undefined,
    qId, // 传递 qId 参数
  });

  // 获取下载按钮回调 - 通过全局状态管理
  const handleDownload = () => {
    // 直接调用全局的 selectContact
    const event = new CustomEvent("globalSelectContact");
    window.dispatchEvent(event);
  };

  // 回到顶部功能
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  // 检查365天内是否已经触发过
  const checkTodayTriggered = () => {
    const lastTriggerDate = localStorage.getItem("secondPageTriggerDate");
    if (!lastTriggerDate) return false;

    const lastDate = new Date(lastTriggerDate);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - lastDate.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return diffDays < 365;
  };

  // 记录已触发
  const markTodayTriggered = () => {
    const now = new Date().toISOString();
    localStorage.setItem("secondPageTriggerDate", now);
    setHasTriggeredToday(true);
  };

  // 第二页触发逻辑 - 与下载详细数据逻辑保持一致，365天内只弹一次
  const handleSecondPageTrigger = () => {
    // 检查365天内是否已经触发过
    if (checkTodayTriggered()) {
      //console.log('🎯 365天内已经触发过第二页逻辑，跳过')
      return;
    }

    //console.log('🎯 滚动到第二页，触发下载详细数据逻辑（365天内首次）')
    // 记录已触发
    markTodayTriggered();
    // 直接调用全局的 selectContact，与下载按钮逻辑保持一致
    const event = new CustomEvent("globalSelectContact");
    window.dispatchEvent(event);
  };

  // 滚动监听逻辑
  useEffect(() => {
    // 初始化检查今天是否已触发
    const todayTriggered = checkTodayTriggered();
    console.log("todayTriggered", todayTriggered);
    setTimeout(() => {
      setHasTriggeredToday(todayTriggered);
    }, 0);

    const handleScroll = () => {
      const scrollTop =
        window.pageYOffset || document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;

      // 计算当前页面（基于视口高度）
      const pageHeight = windowHeight;
      const currentPageNumber = Math.floor(scrollTop / pageHeight) + 1;

      // 检测是否滚动到第二页，且今天未触发过
      if (
        currentPageNumber >= 2 &&
        !hasTriggeredSecondPage &&
        !hasTriggeredToday
      ) {
        setHasTriggeredSecondPage(true);
      }

      // 如果滚动回第一页，重置触发状态（但保持今天的触发记录）
      if (currentPageNumber < 2 && hasTriggeredSecondPage) {
        setHasTriggeredSecondPage(false);
      }

      // 控制回到顶部按钮的显示
      setShowBackToTop(scrollTop > 300);
    };

    // 添加滚动监听
    window.addEventListener("scroll", handleScroll, { passive: true });

    // 初始检查
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [hasTriggeredSecondPage]);

  // Sentinel Ref - 用于检测吸顶状态
  const sentinelRef = React.useRef<HTMLDivElement>(null);

  // 使用 IntersectionObserver 检测 Sentinel 元素来实现精确的吸顶检测
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    // 顶部导航栏高度 + 1px buffer
    // PC: 75px, Mobile: 52px
    const offset = isMobile ? 53 : 76;

    const observer = new IntersectionObserver(
      ([entry]) => {
        // 当 sentinel 滚出视口顶部（boundingClientRect.top < offset）且不再交叉时，说明 header 已经吸顶
        // 注意：这里需要仔细调试逻辑。
        // entry.isIntersecting: true -> 在视口内 (未吸顶)
        // entry.isIntersecting: false && entry.boundingClientRect.top < 0 -> 滚出视口上方 (已吸顶)

        // 简化逻辑：当 sentinel 向下滚动穿过 trigger line 时 -> 吸顶
        // 当 sentinel 向上滚动穿过 trigger line 时 -> 取消吸顶

        // 但 IntersectionObserver 的阈值判定比较微妙。
        // 我们使用一个简单的逻辑：
        // 如果 sentinel 不可见，且位置在上方 -> 吸顶
        const isStuck =
          !entry.isIntersecting && entry.boundingClientRect.top < offset;

        if (lastIsStickyRef.current !== isStuck) {
          lastIsStickyRef.current = isStuck;
          setIsSticky(isStuck);
          setIsFilterCollapsed(isStuck);
        }
      },
      {
        threshold: [0], // 只要有一像素可见就触发
        rootMargin: `-${offset}px 0px 0px 0px`, // 偏移量，模拟 sticky top 位置
      }
    );

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
    };
  }, [isMobile]);

  // 移动端筛选弹窗打开时锁定 body 滚动
  useScrollLock(filterDrawerOpen);

  // 调试信息 - 查看SearchDetail页面数据
  // console.log('🔍 SearchDetail 组件状态:', {
  //   brandId,
  //   industryId,
  //   currentDays,
  //   selectedPlatforms,
  //   flowLoading,
  //   hasBrandComparison: !!brandComparison,
  //   hasReputationKeywords: !!reputationKeywords
  // })

  const {
    keyword,
    setKeyword,
    rows,
    search,
    isLoading: searchLoading,
    error: searchError,
  } = useSearchPanel(searchQuery);
  const [rowsOpen, setRowsOpen] = React.useState(false);
  const navigate = useNavigate();

  // 优化：页面加载时默认不显示搜索下拉列表
  React.useEffect(() => {
    setRowsOpen(false);
  }, []);

  // 行业选项初始化
  React.useEffect(() => {
    if (
      industries.length > 0 &&
      (!industrySel || !industries.includes(industrySel))
    ) {
      setIndustrySel(industries[0]);
    }
  }, [industries, industrySel]);

  // AI平台选择变更处理
  const handlePlatformSelectionChange = async (platform: string) => {
    if (!brandId || !industryId) return;

    // 仅更新状态，数据刷新由 useSearchDetailFlow 的轻量刷新 effect 处理
    if (platform === "all") {
      setSelectedPlatforms([]);
    } else {
      setSelectedPlatforms([platform]);
    }
  };

  const onCloseRows = React.useCallback(() => setRowsOpen(false), []);

  const searchOnce = React.useCallback(
    async (kw?: string) => {
      const result = await search(kw);
      if (result && result.length > 0) {
        setRowsOpen(true);
      }
      return result || [];
    },
    [search]
  );

  const onSearch = React.useCallback(
    async (kw?: string | SearchRow) => {
      try {
        // 检查传入的是否是 SearchRow 对象
        if (typeof kw === "object" && kw !== null && "brand" in kw) {
          // 直接使用传入的 SearchRow 对象
          const row = kw as SearchRow;
          const searchQuery = row.brand;
          const params = new URLSearchParams({
            brand: row.brand,
            brandId: row.brandId || "",
            industry: row.currentCategoryName || row.tags?.[0] || "",
            industryId: row.industryId || "",
            industries: JSON.stringify(row.tags || []),
            industryIds: JSON.stringify(row.industryIds || []),
            q: searchQuery,
            modelName: currentModelId,
          });
          navigate(`/SearchDetail?${params.toString()}`, { replace: true });
          //setKeyword('')  // 注释掉这行，让用户可以继续编辑搜索内容
        } else {
          // 确保使用有效的搜索关键词
          const searchQuery = kw || keyword || "";
          // 只有当接口成功返回时才进行后续处理
          if (searchQuery.length > 0) {
            const searchResults = await search(searchQuery as string);
            console.log("🔍 搜索结果:", searchResults);
            // 处理搜索结果
            const results = Array.isArray(searchResults) ? searchResults : [];
            if (results.length > 0) {
              const row = results[0];
              const params = new URLSearchParams({
                brand: row.brand,
                brandId: row.brandId || "",
                industry: row.currentCategoryName || row.tags?.[0] || "",
                industryId: row.industryId || "",
                industries: JSON.stringify(row.tags || []),
                industryIds: JSON.stringify(row.industryIds || []),
                q: searchQuery,
                modelName: currentModelId,
              });
              navigate(`/SearchDetail?${params.toString()}`, { replace: true });
              //setKeyword('')  // 注释掉这行，让用户可以继续编辑搜索内容
            } else if (results.length === 0 && results != null) {
              // 空结果提示 - 只在接口成功返回且数据为空时显示
              // 修改这里：使用弹窗替代消息提示
              setSearchedBrandName(searchQuery);
              setShowBrandNotFoundModal(true);
            }
          }
        }
      } catch (e) {
        // 错误已通过error状态显示
        console.error("搜索出错:", e);
      }
    },
    [
      search,
      keyword,
      navigate,
      setKeyword,
      currentModelId,
      setShowBrandNotFoundModal,
      setSearchedBrandName,
    ]
  );

  // 将 API 数据映射为表格友好行
  const mediaRows = React.useMemo(() => {
    return (mediaCite || [])
      .slice()
      .sort((a: any, b: any) => (b?.rate || 0) - (a?.rate || 0))
      .map((item: any, idx: number) => ({
        key: idx + 1,
        media: item?.site_name,
        domain: item?.domain,
        domainId: item?.domainId,
        ratio: `${Number(item?.rate || 0).toFixed(2)}%`,
      }));
  }, [mediaCite, brandId, industryId]);

  const contentRows = React.useMemo(() => {
    return (contentCite || [])
      .slice()
      .sort((a: any, b: any) => (b?.rate || 0) - (a?.rate || 0))
      .map((item: any, idx: number) => ({
        key: idx + 1,
        media: item?.title || item?.siteName,
        url: item?.url,
        domain: item?.siteName,
        ratio: `${Number(item?.rate || 0).toFixed(2)}%`,
      }));
  }, [contentCite, brandId, industryId]);

  // 竞品数据概览行数据：从 brandComparison 提取各品牌最新一期的指标
  const competitorRows = React.useMemo(() => {
    if (
      !brandComparison ||
      !brandComparison.brandList ||
      !brandComparison.rateList
    )
      return [] as Array<{
        brand: string;
        mentionRate: number;
        firstMentionRate: number;
        firstRecommendRate: number;
        positiveSentiment: number;
        negativeMentionRate: number;
        isCurrent?: boolean;
      }>;

    const cur = brandComparison.brandList.curBrand;
    const others = brandComparison.brandList.otherBrands || [];
    const ids = [cur, ...others].map((b) => b.brandId);
    const idToName = new Map<string, string>([
      [cur.brandId, cur.brandName],
      ...others.map((b) => [b.brandId, b.brandName] as [string, string]),
    ]);

    const getLatest = (arr?: Array<{ day: string; rate: number }>): number => {
      if (!arr || arr.length === 0) return 0;
      const last = arr[arr.length - 1];
      const val =
        typeof last.rate === "number" ? last.rate : Number(last.rate) || 0;
      return Number(val.toFixed(2));
    };

    const rows = ids.map((id) => {
      const mentionRate = getLatest(brandComparison.rateList.mentionRate?.[id]);
      const firstMentionRate = getLatest(
        brandComparison.rateList.firstMentionRate?.[id]
      );
      const firstRecommendRate = getLatest(
        brandComparison.rateList.firstRecommendationRate?.[id]
      );
      const positive = getLatest(
        brandComparison.rateList.positiveMentionRate?.[id]
      );
      const negative = getLatest(
        brandComparison.rateList.negativeMentionRate?.[id]
      );
      return {
        brand: idToName.get(id) || id,
        mentionRate,
        firstMentionRate,
        firstRecommendRate,
        positiveSentiment: positive,
        negativeMentionRate: negative,
        isCurrent: id === cur.brandId,
      };
    });

    // 当前品牌优先，按提及率降序
    rows.sort(
      (a, b) =>
        (a.isCurrent === b.isCurrent ? 0 : a.isCurrent ? -1 : 1) ||
        b.mentionRate - a.mentionRate
    );
    return rows;
  }, [brandComparison]);

  // 点击下拉搜索结果事件
  const onSelectRow = React.useCallback(
    async (row: any) => {
      // 立即更新本地状态
      const newIndustry = row.currentCategoryName || row.tags?.[0] || "";
      setIndustrySel(newIndustry);

      // 更新URL参数
      const params = new URLSearchParams({
        brand: row.brand,
        brandId: row.brandId || "",
        industry: newIndustry,
        industryId: row.industryId || "",
        industries: JSON.stringify(row.tags || []),
        industryIds: JSON.stringify(row.industryIds || []),
        q: keyword || "",
        modelName: currentModelId,
      });

      navigate(`/SearchDetail?${params.toString()}`, { replace: true });
      // 移除 triggerSearchFlow(true) 调用，依赖 URL 变化自动触发
      setRowsOpen(false); // 关闭搜索下拉列表
    },
    [keyword, navigate, currentModelId]
  );

  const togglePlatform = (key: string) => {
    setSelectedPlatforms((prev) => (prev.includes(key) ? [] : [key]));
  };

  // 时间范围变更处理
  const handleTimeRangeChange = async (
    timeRange:
      | "today"
      | "last7d"
      | "last30d"
      | number
      | { startDate: string; endDate: string }
  ) => {
    if (!brandId || !industryId) return;

    // 更新状态
    setCurrentTimeRange(timeRange);

    // 同步 URL 参数
    const newParams = new URLSearchParams(locationSearch);

    if (typeof timeRange === "object") {
      // 自定义时间范围：设置 startDate 和 endDate
      newParams.set("startDate", timeRange.startDate);
      newParams.set("endDate", timeRange.endDate);
    } else {
      // 预设时间范围：清除 startDate 和 endDate
      newParams.delete("startDate");
      newParams.delete("endDate");
    }

    navigate(`/SearchDetail?${newParams.toString()}`, { replace: true });
  };

  // 添加处理加入内测联盟的函数
  const handleJoinBeta = () => {
    // 触发全局联系事件
    const event = new CustomEvent("globalSelectContact");
    window.dispatchEvent(event);
    // 关闭弹窗
    setShowBrandNotFoundModal(false);
  };

  return (
    <div className={`searchDetailContainer ${isMobile ? "mobile" : ""}`}>
      {/* 背景装饰元素 */}
      <div className="backgroundDecoration" />

      {/* 内容区域 */}
      <div className={`contentWrapper ${isMobile ? "mobile" : ""}`}>
        {/* 内容区域 */}

        {/* Unified Search & Analysis Card */}
        {/* Sentinel 元素 - 用于检测吸顶状态 */}
        <div
          ref={sentinelRef}
          style={{ height: "1px", marginBottom: "-1px" }}
        />
        <div
          className={`cardContainer ${isMobile ? "mobile" : ""} ${isSticky ? "isSticky" : ""}`}
        >
          <div
            className={`unifiedCard ${isMobile ? "mobile" : ""} ${isFilterCollapsed ? "collapsed" : ""}`}
          >
            {/* 顶部搜索区域 - 收起时添加 collapsed 类实现动画 */}
            <div
              className={`searchSection ${isFilterCollapsed ? "collapsed" : ""}`}
            >
              <SearchPanelCard
                value={keyword}
                onChange={setKeyword}
                rows={rows}
                showRows={rowsOpen}
                isLoading={searchLoading}
                error={searchError}
                onRetry={() => searchOnce()}
                onCloseRows={onCloseRows}
                onSearch={onSearch}
                onSelectRow={onSelectRow}
                onFetchSuggestions={searchOnce}
                // Pass prop to indicate it's unified if needed, or rely on CSS overrides
              />
            </div>

            {/* 品牌+行业选择区域 */}
            {/* 品牌+行业选择区域 */}
            <div
              className={`filterSection ${isFilterCollapsed ? "collapsed" : ""}`}
            >
              {isMobile ? (
                <>
                  {/* Mobile Filter Trigger Bar */}
                  <div
                    onClick={() => setFilterDrawerOpen(true)}
                    className="mobileFilterTrigger"
                  >
                    <div className="triggerLeft">
                      <FilterOutlined className="filterIcon" />
                      <span className="triggerLabel">
                        {t("search_analysis.drawer_title")}
                      </span>
                    </div>
                    <div className="triggerRight">
                      <span className="industryLabel">
                        {industrySel
                          ? industrySel
                          : t("search_analysis.all_industries")}
                      </span>
                      <LeftOutlined rotate={-90} className="arrowIcon" />
                    </div>
                  </div>

                  <Drawer
                    title={t("search_analysis.drawer_title")}
                    placement="bottom"
                    onClose={() => setFilterDrawerOpen(false)}
                    open={filterDrawerOpen}
                    height="85vh"
                    className="filterDrawer"
                  >
                    <BaseAnalysisSection
                      isMobile={true}
                      showPlatformControls={true}
                      showBrandIndustryControls={true}
                      brandName={brand || brandName}
                      industry={industrySel}
                      onIndustryChange={setIndustrySel}
                      industryOptions={industries.map((industry: string) => ({
                        value: industry,
                        label: industry,
                      }))}
                      selectedIndustry={industrySel}
                      onIndustrySelectionChange={async (selectedIndustry) => {
                        if (selectedIndustry === industrySel) return;
                        const selectedIndex = industries.findIndex(
                          (ind: string) => ind === selectedIndustry
                        );
                        if (selectedIndex !== -1) {
                          const selectedIndustryId = industryIds[selectedIndex];
                          setIndustrySel(selectedIndustry);
                          const newParams = new URLSearchParams(locationSearch);
                          newParams.set("industry", selectedIndustry);
                          newParams.set("industryId", selectedIndustryId || "");
                          navigate(`/SearchDetail?${newParams.toString()}`, {
                            replace: true,
                          });
                          try {
                            await triggerSearchFlow();
                          } catch (error) {
                            console.error("搜索出错:", error);
                          }
                        } else {
                          setIndustrySel(selectedIndustry);
                        }
                      }}
                      models={models}
                      modelsLoading={flowLoading}
                      onPlatformChange={(platform) => {}}
                      brandMetrics={brandMetrics}
                      brandMetricsLoading={flowLoading}
                      onTimeRangeChange={handleTimeRangeChange}
                      onPlatformSelectionChange={handlePlatformSelectionChange}
                      initialStartDate={startDate}
                      initialEndDate={endDate}
                      initialQId={qId}
                      brandId={brandId}
                      categoryId={industryId}
                      onQIdChange={(newQId) => {
                        const newParams = new URLSearchParams(locationSearch);
                        if (newQId) {
                          newParams.set("qId", newQId);
                        } else {
                          newParams.delete("qId");
                        }
                        navigate(`/SearchDetail?${newParams.toString()}`, {
                          replace: true,
                        });
                      }}
                    />
                  </Drawer>
                </>
              ) : (
                <BaseAnalysisSection
                  isMobile={isMobile}
                  showPlatformControls={true}
                  showBrandIndustryControls={true}
                  brandName={brand || brandName}
                  industry={industrySel}
                  onIndustryChange={setIndustrySel}
                  industryOptions={industries.map((industry: string) => ({
                    value: industry,
                    label: industry,
                  }))}
                  selectedIndustry={industrySel}
                  onIndustrySelectionChange={async (selectedIndustry) => {
                    if (selectedIndustry === industrySel) return;
                    const selectedIndex = industries.findIndex(
                      (ind: string) => ind === selectedIndustry
                    );
                    if (selectedIndex !== -1) {
                      const selectedIndustryId = industryIds[selectedIndex];
                      setIndustrySel(selectedIndustry);
                      const newParams = new URLSearchParams(locationSearch);
                      newParams.set("industry", selectedIndustry);
                      newParams.set("industryId", selectedIndustryId || "");
                      navigate(`/SearchDetail?${newParams.toString()}`, {
                        replace: true,
                      });
                      try {
                        await triggerSearchFlow();
                      } catch (error) {
                        console.error("搜索出错:", error);
                      }
                    } else {
                      setIndustrySel(selectedIndustry);
                    }
                  }}
                  models={models}
                  modelsLoading={flowLoading}
                  onPlatformChange={(platform) => {}}
                  brandMetrics={brandMetrics}
                  brandMetricsLoading={flowLoading}
                  onTimeRangeChange={handleTimeRangeChange}
                  onPlatformSelectionChange={handlePlatformSelectionChange}
                  initialStartDate={startDate}
                  initialEndDate={endDate}
                  initialQId={qId}
                  brandId={brandId}
                  categoryId={industryId}
                  onQIdChange={(newQId) => {
                    const newParams = new URLSearchParams(locationSearch);
                    if (newQId) {
                      newParams.set("qId", newQId);
                    } else {
                      newParams.delete("qId");
                    }
                    navigate(`/SearchDetail?${newParams.toString()}`, {
                      replace: true,
                    });
                  }}
                  onCollapseChange={setIsFilterCollapsed}
                  externalCollapsed={isFilterCollapsed}
                />
              )}
            </div>
          </div>
        </div>

        {/* Other Analysis Sections */}
        <div className={`analysisSectionsWrapper ${isMobile ? "mobile" : ""}`}>
          <InsightAnalysisSection
            brandName={brandName}
            brandId={brandId}
            categoryId={industryId}
            selectedCompetitors={selectedCompetitors}
            setSelectedCompetitors={setSelectedCompetitors}
            selectedPlatforms={selectedPlatforms}
            brandAiMetricsTrend={brandAiMetricsTrend as any}
            brandMetrics={brandMetrics}
            brandMetricsLoading={flowLoading}
            brandComparison={brandComparison} // 新增：传递品牌对比数据
            brandRatesList={brandRatesList}
            days={currentDays} // 新增：传递时间范围天数
            onDownload={handleDownload}
          />

          <SentimentAnalysisSection
            brandId={brandId}
            brandName={brandName}
            categoryId={industryId}
            days={currentDays}
            modelId={selectedPlatforms?.length ? selectedPlatforms[0] : "all"}
            selectedPlatforms={selectedPlatforms} // 新增：传递选中的平台列表
            rates={brandMetrics}
            trend={brandMetricsTrend}
            brandComparison={brandComparison}
            brandRatesList={brandRatesList}
            reputationKeywords={reputationKeywords} // 新增：传递品牌声誉关键词数据
            onDownload={handleDownload} // 新增：传递下载按钮回调
          />
          <CitationAnalysisSection
            brandName={brandName}
            brandId={brandId}
            categoryId={industryId}
            days={currentDays}
            modelId={selectedPlatforms?.length ? selectedPlatforms[0] : "all"}
            mediaRows={mediaRows}
            contentRows={contentRows}
          />
          <CompetitorOverviewSection
            rows={competitorRows}
            brandRatesList={brandRatesList}
            onDownload={handleDownload}
          />
        </div>

        {/* 免责声明 */}
        <div className={`disclaimerWrapper ${isMobile ? "mobile" : ""}`}>
          <Disclaimer />
        </div>
      </div>
      {/* 回到顶部按钮 */}
      {showBackToTop && (
        <div className="backToTop" onClick={scrollToTop}>
          <img src={topIcon} alt="回到顶部" className="topIconImg" />
        </div>
      )}
      {/* 品牌未收录弹窗 */}
      <BrandNotFoundModal
        open={showBrandNotFoundModal}
        onClose={() => setShowBrandNotFoundModal(false)}
        onJoinBeta={handleJoinBeta}
        brandName={searchedBrandName}
      />
    </div>
  );
};

export default SearchDetail;
